/**
 * "What do you do in the shop?" — the multi-select shared by signup,
 * invitations and Team Management.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY ONE COMPONENT FOR ALL THREE
 * ═══════════════════════════════════════════════════════════════════════════
 * Signup, the invite dialog and the member editor ask the same question, and
 * if each built its own answer they would drift — one would forget Detail
 * Specialist, another would quietly offer OWNER as a job. One component means
 * the vocabulary is defined once and the same set is representable everywhere.
 *
 * AUTHORITY IS NOT ON THIS LIST. OWNER, MANAGER and the rest are ACCESS, asked
 * separately, and a person's answer here can never change what they may
 * administer. That separation is the entire point of the campaign, so it is
 * expressed as "these options do not exist here" rather than as a check.
 *
 * ZERO SELECTED IS VALID. A pure owner who does no operational work has an
 * empty set, and the copy says so instead of nagging them into picking one.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * FORM FACTOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Full-width stacked rows on a phone, two columns from sm up. Each row is a
 * whole-row label-wrapped target rather than a small checkbox, because these
 * are thumb targets on a shop floor, and the control is never hidden or
 * disabled by viewport — a manager on a phone can do what a manager on a
 * desktop can.
 */
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { WORK_ROLES, WORK_ROLE_LABELS, type WorkRole } from '@/lib/roles/multiRole';
import { workRolesToRender, isOfferedWorkRole } from '@/lib/roles/offeredRoles';

/** One line of plain English per role, so the choice is not a guess. */
const WORK_ROLE_HINTS: Readonly<Record<WorkRole, string>> = {
  TECHNICIAN: 'Performs mechanical, diagnostic and maintenance work.',
  SERVICE_ADVISOR: 'Writes and authorises repair orders, and talks to customers.',
  DISPATCHER: 'Assigns and sequences work across the board.',
  PARTS: 'Orders, receives and issues parts.',
  DETAIL_SPECIALIST: 'Performs detailing and wash work only — not mechanical repair.',
};

export interface WorkRoleMultiSelectProps {
  value: readonly WorkRole[];
  onChange: (next: WorkRole[]) => void;
  disabled?: boolean;
  /** Heading. Signup asks the question; the editor states the section. */
  label?: string;
  description?: string;
  className?: string;
  idPrefix?: string;
}

export function WorkRoleMultiSelect({
  value,
  onChange,
  disabled = false,
  label = 'What do you do in the shop?',
  description = 'Choose every job you perform. You can change this later, and leaving it empty is fine if you do not work on vehicles yourself.',
  className,
  idPrefix = 'work-role',
}: WorkRoleMultiSelectProps) {
  const selected = new Set(value);

  // WHAT TO DRAW vs WHAT TO STORE — deliberately two different lists.
  //
  // The shop is currently offered Service Advisor, Technician and Detail
  // Specialist. Dispatcher and Parts remain fully supported in the model and in
  // storage; they are simply not handed out today. `workRolesToRender` adds back
  // any role THIS person already holds, so an editor never hides a role it is
  // about to save, and the reconstruction below still runs over the CANONICAL
  // list — which is what keeps a hidden role from being deleted by a save that
  // never rendered it.
  const rendered = workRolesToRender(value);

  // Toggling one role must never disturb the others — that is the whole
  // difference from the scalar this replaces.
  const toggle = (role: WorkRole) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(role)) next.delete(role); else next.add(role);
    onChange(WORK_ROLES.filter((r) => next.has(r)));
  };

  return (
    <div className={cn('space-y-3', className)} data-testid="work-role-multiselect">
      <div className="space-y-1">
        <p className="text-sm font-medium">{label}</p>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
      </div>

      {/* Stacked on a phone, two columns from sm up. */}
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {rendered.map((role) => {
          const id = `${idPrefix}-${role}`;
          const isOn = selected.has(role);
          return (
            <Label
              key={role}
              htmlFor={id}
              className={cn(
                // The whole row is the target, not the 16px box.
                'flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors',
                isOn ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent/40',
                disabled && 'cursor-not-allowed opacity-60',
              )}
              data-testid={`work-role-option-${role}`}
              data-selected={isOn ? 'true' : 'false'}
            >
              <Checkbox
                id={id}
                checked={isOn}
                disabled={disabled}
                onCheckedChange={() => toggle(role)}
                className="mt-0.5"
              />
              <span className="space-y-0.5">
                <span className="block text-sm font-medium leading-none">
                  {WORK_ROLE_LABELS[role]}
                  {!isOfferedWorkRole(role) && (
                    <span
                      className="ml-2 text-xs font-normal text-muted-foreground"
                      data-testid={`work-role-legacy-${role}`}
                    >
                      (existing)
                    </span>
                  )}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {WORK_ROLE_HINTS[role]}
                </span>
              </span>
            </Label>
          );
        })}
      </div>

      {selected.size === 0 && (
        <p className="text-xs text-muted-foreground" data-testid="work-role-none-selected">
          No work roles selected. That is valid — this person will not be offered
          vehicle work.
        </p>
      )}
    </div>
  );
}

export default WorkRoleMultiSelect;
